import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { money, num, pct } from "@owners/format";
import { api } from "../api";
import { HotelPicker, MoneyVar, hotelParam } from "../components/Numbers";
import { StatementReview } from "../components/StatementReview";
import { useSearchParams } from "react-router-dom";
import { useMe, usePrefs } from "../api";

/**
 * Financial Reports (SPEC §11). Six reports:
 *  - Profit & loss: for OCI hotels, straight from the books (Meridian), actual
 *    vs budget vs last year, several hotels added together.
 *  - STR: for OCI hotels, the monthly competitive-set benchmarks Meridian already
 *    ingests (occupancy / ADR / RevPAR vs the comp set, MPI / ARI / RGI), per
 *    hotel; any STR files uploaded in the Dataroom are listed under them.
 *  - Balance sheet, cash flow, aged payables, aged receivables: the files
 *    HMA Users upload in the Dataroom (Financial Reports folder).
 * Bank statements & reconciliations are never shown on the page; they appear
 * only under Download reports, and only for levels allowed to have them.
 */

export const FIN_REPORTS = [
  ["pnl", "Profit & loss statement"], ["balance_sheet", "Balance sheet"], ["cash_flow", "Cash flow statement"],
  ["aged_payables", "Aged payables"], ["aged_receivables", "Aged receivables"], ["str", "STR report"],
] as const;
const REPORTS = FIN_REPORTS;
type ReportKey = (typeof REPORTS)[number][0];

type View = "current_month" | "last_month" | "ytd" | "prior_year_end";
const VIEWS: [View, string][] = [["current_month", "Current month"], ["last_month", "Last month"], ["ytd", "YTD"], ["prior_year_end", "Year end"]];

interface Trio { actual: number | null; budget: number | null; lastYear: number | null }
interface PnlRes {
  label: string; period: string;
  hotels: Array<{ id: string; name: string; hasData: boolean; periodClosed: boolean | null;
    summary: null | { roomRevenue: Trio; totalRevenue: Trio; gop: Trio | null; noi: Trio | null } }>;
  booksUpdatedAt: string | null;
  rows: Array<{ key: string; label: string; kind: string; sign: "rev" | "exp" | "profit" | "stat" } & Trio>;
  stats: Record<"roomsAvailable" | "roomsSold" | "roomRevenue" | "totalRevenue", Trio> | null;
}
interface Doc { id: string; hotelId: string; hotelName: string; docType: string; label: string; fileName: string; contentType: string; uploadedAt: string }
interface DocsRes { statements: Doc[]; downloads: Doc[]; bankStatementsOffered: boolean }

const lastMonthIso = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

export function FinancialReports() {
  const [params, setParams] = useSearchParams();
  // The report lives in the URL (?r=) so the left-menu sub-items can link to it.
  const report: ReportKey = REPORTS.some(([k]) => k === params.get("r")) ? (params.get("r") as ReportKey) : "pnl";
  const setReport = (k: ReportKey) => setParams((p) => { const n = new URLSearchParams(p); n.set("r", k); return n; });
  const [ids, setIds] = useState<string[]>([]);
  const me = useMe().data;
  const reviewer = !!me && (me.level === "admin" || me.capabilities.includes("review_statements"));
  return (
    <>
      <div className="eyebrow">Owners Module · Financial Reports</div>
      {reviewer && <StatementReview admin={me!.level === "admin"} startOpen={params.get("review") === "1"} />}
      <h1>{REPORTS.find(([k]) => k === report)![1]}</h1>
      <div className="tabs">
        {REPORTS.map(([k, l]) => <button key={k} className={report === k ? "on" : ""} onClick={() => setReport(k)}>{l}</button>)}
      </div>
      <div className="row" style={{ marginBottom: 16 }}><HotelPicker value={ids} onChange={setIds} /></div>
      {report === "pnl" ? <PnlView ids={ids} /> : report === "str" ? <><StrView ids={ids} /><Uploaded ids={ids} docType="str" /></> : <Uploaded ids={ids} docType={report} />}
      <Downloads ids={ids} />
    </>
  );
}

type Pick = { view: View } | { period: string; block: "month" | "ytd" };
const pickQuery = (p: Pick) => ("view" in p ? `view=${p.view}` : `period=${p.period}&block=${p.block}`);

function PnlView({ ids }: { ids: string[] }) {
  const prefs = usePrefs().data;
  const [picked, setPick] = useState<Pick | null>(null);
  const pick: Pick = picked ?? { view: prefs?.pnlView ?? "last_month" };    // Settings → Default views
  const [grid, setGrid] = useState(false);
  const view = "view" in pick ? pick.view : null;
  const setView = (v: View) => setPick({ view: v });
  const q = useQuery({ queryKey: ["pnl", pickQuery(pick), ids], queryFn: () => api<PnlRes>(`/api/financials/pnl?${pickQuery(pick)}&hotelIds=${hotelParam(ids)}`) });
  const d = q.data;
  const missing = d?.hotels.filter((h) => !h.hasData) ?? [];
  const open = d?.hotels.some((h) => h.hasData && h.periodClosed === false);
  const stat = (k: keyof NonNullable<PnlRes["stats"]>) => d?.stats?.[k];
  const ratio = (a: number | null | undefined, b: number | null | undefined) => (a != null && b ? a / b : null);

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <div className="row">
          <div className="seg">{VIEWS.map(([k, l]) => <button key={k} className={view === k ? "on" : ""} onClick={() => setView(k)}>{l}</button>)}</div>
          <button type="button" className={`chip${view === null || grid ? " on" : ""}`} aria-expanded={grid} onClick={() => setGrid(!grid)}>Pick a month ▾</button>
        </div>
        {d && <span className="muted">{d.label}{open ? " · books not yet closed" : ""}</span>}
      </div>
      {grid && <MonthGrid pick={pick} onPick={(p) => { setPick(p); setGrid(false); }} />}
      {q.isLoading ? <p className="muted">Loading…</p> : q.error ? <p className="error">{(q.error as Error).message}</p> : d && (
        d.rows.length === 0 ? <p className="muted">No profit & loss numbers for these hotels and this period yet.</p> : (
          <>
            {d.stats && (
              <p className="muted" style={{ marginTop: 0 }}>
                Occupancy {pct(ratio(stat("roomsSold")?.actual, stat("roomsAvailable")?.actual), { ratio: true })}
                {" · "}ADR {money(ratio(stat("roomRevenue")?.actual, stat("roomsSold")?.actual), { decimals: 2 })}
                {" · "}RevPAR {money(ratio(stat("roomRevenue")?.actual, stat("roomsAvailable")?.actual), { decimals: 2 })}
              </p>
            )}
            <table className="fin">
              <thead>
                <tr><th /><th className="num">Actual</th><th className="num">Budget</th><th className="num">vs budget</th>
                  <th className="num">Last year</th><th className="num">vs last year</th></tr>
              </thead>
              <tbody>
                {d.rows.map((r) => r.kind === "header" ? (
                  <tr key={r.key} className="k-header"><td colSpan={6}>{r.label}</td></tr>
                ) : (
                  <tr key={r.key} className={`k-${r.kind}`}>
                    <td>{title(r.label)}</td>
                    <td className="num">{money(r.actual)}</td>
                    <td className="num">{money(r.budget)}</td>
                    <td className="num"><MoneyVar actual={r.actual} base={r.budget} cost={r.sign === "exp"} /></td>
                    <td className="num">{money(r.lastYear)}</td>
                    <td className="num"><MoneyVar actual={r.actual} base={r.lastYear} cost={r.sign === "exp"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <HotelSummary d={d} />
            {missing.length > 0 && (
              <p className="muted" style={{ fontSize: 12 }}>
                Not included (no numbers for this period yet): {missing.map((h) => h.name).join(", ")}.
              </p>
            )}
            {d.booksUpdatedAt && <p className="muted" style={{ fontSize: 12 }}>Books last updated {new Date(d.booksUpdatedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}.</p>}
          </>
        )
      )}
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Demo: month × year grid — five years, Jan–Dec plus YE (year-end, Jan–Dec added up). Future months are disabled. */
function MonthGrid({ pick, onPick }: { pick: Pick; onPick: (p: Pick) => void }) {
  const [block, setBlock] = useState<"month" | "ytd">("block" in pick ? pick.block : "month");
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);
  const sel = "period" in pick ? pick : null;
  const cell = (period: string, b: "month" | "ytd", text: string) => (
    <button key={period + b} type="button" disabled={period > thisMonth}
      className={sel && sel.period === period && sel.block === b ? "on" : ""}
      onClick={() => onPick({ period, block: b })}>{text}</button>
  );
  return (
    <div className="month-grid">
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="seg">
          <button className={block === "month" ? "on" : ""} onClick={() => setBlock("month")}>The month</button>
          <button className={block === "ytd" ? "on" : ""} onClick={() => setBlock("ytd")}>Year to date through it</button>
        </div>
      </div>
      <table>
        <thead><tr><th />{MONTHS.map((m) => <th key={m}>{m}</th>)}<th>YE</th></tr></thead>
        <tbody>
          {years.map((y) => (
            <tr key={y}>
              <th>{y}</th>
              {MONTHS.map((m, i) => <td key={m}>{cell(`${y}-${String(i + 1).padStart(2, "0")}`, block, m)}</td>)}
              <td>{y < now.getFullYear() ? cell(`${y}-12`, "ytd", "YE") : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Demo: hotel-by-hotel summary under the combined P&L (only when more than one hotel has numbers). */
function HotelSummary({ d }: { d: PnlRes }) {
  const rows = d.hotels.filter((h) => h.summary);
  if (rows.length < 2) return null;
  const cell = (t: Trio | null | undefined, cost = false) => (
    <><td className="num">{money(t?.actual ?? null)}</td><td className="num"><MoneyVar actual={t?.actual ?? null} base={t?.budget ?? null} cost={cost} /></td></>
  );
  return (
    <>
      <h2 style={{ marginTop: 22 }}>By hotel</h2>
      <table className="fin">
        <thead>
          <tr><th>Hotel</th>
            <th className="num">Rooms revenue</th><th className="num">vs budget</th>
            <th className="num">Total revenue</th><th className="num">vs budget</th>
            <th className="num">GOP</th><th className="num">vs budget</th>
            <th className="num">NOI</th><th className="num">vs budget</th></tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.id}>
              <td>{h.name}{h.periodClosed === false && <span className="muted" style={{ fontSize: 11 }}> · not closed</span>}</td>
              {cell(h.summary!.roomRevenue)}{cell(h.summary!.totalRevenue)}{cell(h.summary!.gop)}{cell(h.summary!.noi)}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

interface StrPoint { occ: number | null; adr: number | null; revpar: number | null }
interface StrPeriod {
  periodStart: string; periodEnd: string; property: StrPoint; compSet: StrPoint;
  index: { mpi: number | null; ari: number | null; rgi: number | null };
  changeYoY: { occ: number | null; adr: number | null; revpar: number | null };
  ytd: { occ: number | null; adr: number | null; revpar: number | null; mpi: number | null; rgi: number | null };
  running12: { occ: number | null; mpi: number | null; rgi: number | null };
}
interface StrRes { from: string; to: string; hotels: Array<{ id: string; name: string; months: Array<StrPeriod & { month: string }>; latestWeek: StrPeriod | null }> }

const idx = (v: number | null) => (v == null ? "—" : v.toFixed(1));
const idxColor = (v: number | null) => (v == null ? undefined : v >= 100 ? "var(--green, #2e7d32)" : "var(--red, #c62828)");
const monthLabel = (m: string) => new Date(m + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });

/** STR (SPEC §11): competitive-set benchmarks per hotel — never added across hotels (an average of indexes means nothing). */
function StrView({ ids }: { ids: string[] }) {
  const q = useQuery({ queryKey: ["fin-str", ids], queryFn: () => api<StrRes>(`/api/financials/benchmarks?hotelIds=${hotelParam(ids)}`) });
  const d = q.data;
  const withData = d?.hotels.filter((h) => h.months.length > 0) ?? [];
  const without = d?.hotels.filter((h) => h.months.length === 0) ?? [];
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {q.isLoading ? <p className="muted">Loading…</p> : q.error ? <p className="error">{(q.error as Error).message}</p> : d && (
        withData.length === 0 ? <p className="muted">No STR benchmark reports for these hotels yet.</p> : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>Latest month per hotel. Index 100 = fair share of the competitive set; above 100 (green) beats it.</p>
            <table className="fin">
              <thead>
                <tr><th>Hotel</th><th>Month</th>
                  <th className="num">Occ</th><th className="num">Comp occ</th><th className="num">MPI</th>
                  <th className="num">ADR</th><th className="num">Comp ADR</th><th className="num">ARI</th>
                  <th className="num">RevPAR</th><th className="num">Comp RevPAR</th><th className="num">RGI</th>
                  <th className="num">RGI YTD</th><th className="num">RGI 12 mo</th></tr>
              </thead>
              <tbody>
                {withData.map((h) => {
                  const m = h.months[h.months.length - 1]!;
                  return (
                    <tr key={h.id}>
                      <td>{h.name}</td><td className="muted">{monthLabel(m.month)}</td>
                      <td className="num">{pct(m.property.occ, { ratio: true })}</td><td className="num">{pct(m.compSet.occ, { ratio: true })}</td>
                      <td className="num" style={{ color: idxColor(m.index.mpi) }}>{idx(m.index.mpi)}</td>
                      <td className="num">{money(m.property.adr, { decimals: 2 })}</td><td className="num">{money(m.compSet.adr, { decimals: 2 })}</td>
                      <td className="num" style={{ color: idxColor(m.index.ari) }}>{idx(m.index.ari)}</td>
                      <td className="num">{money(m.property.revpar, { decimals: 2 })}</td><td className="num">{money(m.compSet.revpar, { decimals: 2 })}</td>
                      <td className="num" style={{ color: idxColor(m.index.rgi), fontWeight: 600 }}>{idx(m.index.rgi)}</td>
                      <td className="num">{idx(m.ytd.rgi)}</td><td className="num">{idx(m.running12.rgi)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <h3 style={{ marginTop: 18 }}>RGI by month</h3>
            <table className="fin">
              <thead><tr><th>Hotel</th>{monthsBetween(d.from, d.to).map((m) => <th key={m} className="num">{monthLabel(m)}</th>)}</tr></thead>
              <tbody>
                {withData.map((h) => {
                  const by = new Map(h.months.map((m) => [m.month, m.index.rgi]));
                  return <tr key={h.id}><td>{h.name}</td>{monthsBetween(d.from, d.to).map((m) => <td key={m} className="num" style={{ color: idxColor(by.get(m) ?? null) }}>{idx(by.get(m) ?? null)}</td>)}</tr>;
                })}
              </tbody>
            </table>
            {withData.some((h) => h.latestWeek) && (
              <p className="muted" style={{ fontSize: 12 }}>
                Latest week: {withData.filter((h) => h.latestWeek).map((h) => `${h.name} RGI ${idx(h.latestWeek!.index.rgi)} (week of ${h.latestWeek!.periodStart})`).join(" · ")}
              </p>
            )}
            {without.length > 0 && <p className="muted" style={{ fontSize: 12 }}>No benchmark reports: {without.map((h) => h.name).join(", ")}.</p>}
          </>
        )
      )}
    </div>
  );
}
function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from; m <= to && out.length < 24; ) { out.push(m); const d = new Date(m + "-01T12:00:00Z"); d.setUTCMonth(d.getUTCMonth() + 1); m = d.toISOString().slice(0, 7); }
  return out;
}

function Uploaded({ ids, docType }: { ids: string[]; docType: ReportKey }) {
  const [period, setPeriod] = useState(lastMonthIso());
  const q = useQuery({ queryKey: ["fin-docs", period, ids], queryFn: () => api<DocsRes>(`/api/financials/documents?period=${period}&hotelIds=${hotelParam(ids)}`) });
  const docs = (q.data?.statements ?? []).filter((d) => d.docType === docType);
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <label className="muted">Month <input type="month" value={period} max={new Date().toISOString().slice(0, 7)} onChange={(e) => e.target.value && setPeriod(e.target.value)} /></label>
      </div>
      {q.isLoading ? <p className="muted">Loading…</p> : q.error ? <p className="error">{(q.error as Error).message}</p> : docs.length === 0 ? (
        <p className="muted">Nothing uploaded for this month yet.</p>
      ) : (
        <table>
          <thead><tr><th>Hotel</th><th>File</th><th>Uploaded</th><th /></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td>{d.hotelName}</td><td>{d.fileName}</td>
                <td className="muted">{new Date(d.uploadedAt).toLocaleDateString("en-US", { dateStyle: "medium" })}</td>
                <td><a href={`/api/documents/${d.id}/download`}>{d.contentType === "application/pdf" ? "View" : "Get"}</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Download reports (SPEC §11): every statement on file for the month, plus bank statements for levels allowed them. */
function Downloads({ ids }: { ids: string[] }) {
  const [period, setPeriod] = useState(lastMonthIso());
  const q = useQuery({ queryKey: ["fin-docs", period, ids], queryFn: () => api<DocsRes>(`/api/financials/documents?period=${period}&hotelIds=${hotelParam(ids)}`) });
  const all = [...(q.data?.statements ?? []), ...(q.data?.downloads ?? [])];
  return (
    <div className="card">
      <h2>Download reports</h2>
      <div className="row" style={{ marginBottom: 10 }}>
        <label className="muted">Month <input type="month" value={period} max={new Date().toISOString().slice(0, 7)} onChange={(e) => e.target.value && setPeriod(e.target.value)} /></label>
        <span className="muted">{num(all.length)} files on file</span>
      </div>
      {all.length === 0 ? <p className="muted">No files for this month yet.</p> : (
        <table>
          <thead><tr><th>Hotel</th><th>Report</th><th>File</th><th /></tr></thead>
          <tbody>
            {all.map((d) => (
              <tr key={d.id}><td>{d.hotelName}</td><td>{d.label}</td><td>{d.fileName}</td>
                <td><a href={`/api/documents/${d.id}/download`}>Download</a></td></tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted" style={{ fontSize: 12 }}>
        Profit & loss numbers above come from the books. Combined packages (one file per hotel or all hotels, PDF + Excel) arrive with My Reports.
        {q.data && !q.data.bankStatementsOffered ? "" : " Bank statements and reconciliations are listed here only, never on the page."}
      </p>
    </div>
  );
}

/** "ROOMS" → "Rooms", "TOTAL REVENUE" → "Total revenue"; keeps F&B, A&G etc. */
function title(s: string): string {
  if (s.trim() === "EBITDA") return "Net operating income (EBITDA)";   // the NOI Today's Update reports
  const t = s.toLowerCase().replace(/(^|[\s(\-/])([a-z])/g, (_m, p, c) => p + c.toUpperCase());
  return t.replace(/\bF&b\b/g, "F&B").replace(/\bA&g\b/g, "A&G").replace(/\bEbitda\b/g, "EBITDA").replace(/\bS&m\b/g, "S&M")
    .replace(/\bPom\b/g, "POM").replace(/\bIt\b/g, "IT");
}
